import AuthLayout from "@/layouts/AuthLayout";
import LoadingScreen from "@c/shared/LoadingScreen";
import { FLOOR_MODAL_ID } from "@c/modals/floorModalId";
import { TourProvider } from "@ctx/TourContext";
import { ViewerProvider } from "@ctx/ViewerContext";
import {
  useAuth,
  useCurrentScene,
  useTour,
  useTourAllScenes,
  useTourData,
  useTourScene,
} from "@h/useCtx";
import { usePublicTour } from "@h/viewer/usePublicTour";
import PasswordPage from "@p/auth/PasswordPage";
import StatusScreen from "@p/_system/StatusScreen";
import Button from "@ui/button";
import { ArrowLeft, Camera, Home, MapPinOff, Plus, ShieldX } from "lucide-react";
import { Suspense, lazy, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { buildRoute } from "@u/routes";
import { extractSubdomain } from "@u/subdomain";

// Lazy: a static FloorModal import drags the viewer HUD graph (FloorMap →
// three hooks → vendor-three) into the eager entry chunk of every page,
// landings included. It only mounts inside viewer/editor, where the three
// chunk is loading anyway.
const FloorModal = lazy(() => import("@c/modals/FloorModal"));

export function AdminGuard({ children }: { children: React.ReactNode }) {
  const { isAdmin, loadingAuth } = useAuth();
  if (loadingAuth) return <LoadingScreen />;
  if (!isAdmin) return <Navigate to={buildRoute.tours()} replace />;
  return <>{children}</>;
}

export function PublicGuard({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { shortId } = useParams<{ shortId: string }>();
  const [searchParams] = useSearchParams();
  const [initialSceneId] = useState(() => searchParams.get("scene") ?? undefined);
  const [initialEditMode] = useState(() => searchParams.get("editMode") === "1");
  const { tour, loading, isLocked, isEmbedBlocked, checkPassword } =
    usePublicTour(shortId);

  if (loading) return <LoadingScreen />;

  if (isEmbedBlocked)
    return (
      <StatusScreen
        title={t("embed_blocked_title")}
        badge="EMBED"
        bgText="EMBED"
        icon={ShieldX}
        subtitle={t("embed_blocked_subtitle")}
        description={t("embed_blocked_desc")}
        actions={[
          {
            label: t("open_tour"),
            icon: ArrowLeft,
            design: "default",
            onClick: () => window.open(window.location.href, "_blank"),
          },
        ]}
      />
    );

  if (!tour) {
    return (
      <StatusScreen
        title="404"
        badge="LOST"
        bgText="404"
        icon={MapPinOff}
        subtitle={t("tour_not_found_title")}
        description={t("tour_not_found_desc")}
        actions={[
          {
            label: t("back"),
            icon: ArrowLeft,
            design: "outline",
            onClick: () => window.history.back(),
          },
          {
            label: t("home"),
            icon: Home,
            to: "/",
            design: "default",
          },
        ]}
      />
    );
  }

  if (isLocked)
    return (
      <AuthLayout>
        <PasswordPage onConfirm={checkPassword} tourTitle={tour.title} />
      </AuthLayout>
    );
  return (
    <TourProvider
      tourId={tour.id}
      initialData={tour}
      initialSceneId={initialSceneId}
    >
      <ViewerProvider initialEditMode={initialEditMode}>
        {children}
        <Suspense fallback={null}>
          <FloorModal id={FLOOR_MODAL_ID} />
        </Suspense>
      </ViewerProvider>
    </TourProvider>
  );
}

/**
 * NOT a guard despite the old name (`EditorGuard`): it performs no auth and no
 * role check — there has never been an `editor` role (profiles.role is
 * admin|client|suspended). Access to /dashboard/tours/:id comes from
 * `RequireAuth` on the parent route plus RLS. This only mounts the tour +
 * viewer contexts in edit mode.
 */
export function TourEditorProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialSceneId = searchParams.get("scene") ?? undefined;

  if (!id) return <Navigate to={buildRoute.tours()} replace />;

  return (
    <TourProvider key={id} tourId={id} initialSceneId={initialSceneId}>
      <ViewerProvider initialEditMode={true}>
        {children}
        <Suspense fallback={null}>
          <FloorModal id={FLOOR_MODAL_ID} />
        </Suspense>
      </ViewerProvider>
    </TourProvider>
  );
}

export function ViewerGuard({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loadingScene } = useTourScene();
  const currentScene = useCurrentScene();
  const { isOwner } = useTourData();
  const tour = useTour();
  const allScenes = useTourAllScenes();
  const [textureReady, setTextureReady] = useState(false);

  useEffect(() => {
    if (!currentScene?.scene_img_url) return;
    const url = currentScene.scene_img_url;
    let cancelled = false;
    // Dynamic import: guards.tsx is in the EAGER route graph — a static
    // textureCache import dragged vendor-three (~964KB gz) into the entry
    // bundle for every page, landings included. ViewerGuard only runs inside
    // the viewer, where the three chunk is loading anyway.
    void import("@/lib/textureCache").then(({ getTexture }) => {
      if (cancelled) return;
      const cached = getTexture(url);
      if (cached) {
        // Diferir para evitar setState síncrono en el effect
        setTimeout(() => {
          if (!cancelled) setTextureReady(true);
        }, 0);
        return;
      }
      getTexture(url, () => {
        if (!cancelled) setTextureReady(true);
      });
    });
    return () => {
      cancelled = true;
    };
  }, [currentScene?.scene_img_url]);
  if (
    loadingScene ||
    (!currentScene && allScenes.length > 0) ||
    (currentScene && !textureReady)
  ) {
    return <LoadingScreen />;
  }

  if (!loadingScene && allScenes.length === 0 && tour) {
    return (
      <StatusScreen
        title={
          isOwner ? t("tour_empty_admin_title") : t("tour_empty_public_title")
        }
        subtitle={
          isOwner
            ? t("tour_empty_admin_subtitle")
            : t("tour_empty_public_subtitle")
        }
        description={
          isOwner ? t("tour_empty_admin_desc") : t("tour_empty_public_desc")
        }
        icon={Camera}
        bgText="EMPTY"
        badge={isOwner ? "EDITOR" : undefined}
        actions={
          isOwner
            ? [
                {
                  label: t("add_first_scene"),
                  icon: Plus,
                  design: "default",
                  to: buildRoute.detail(tour.id),
                },
              ]
            : [
                {
                  label: t("back"),
                  icon: ArrowLeft,
                  design: "outline",
                  onClick: () => navigate(-1),
                },
              ]
        }
      />
    );
  }

  return <>{children}</>;
}

export function RequireAuth() {
  const { user, loadingAuth } = useAuth();
  // A vanity host (`<slug>.andarlabs.com`) serves only public surfaces. Any
  // authenticated route bounces to the main domain, where auth cookies live.
  const vanitySlug =
    typeof window !== "undefined"
      ? extractSubdomain(window.location.hostname)
      : null;
  useEffect(() => {
    if (!vanitySlug) return;
    const main =
      (import.meta.env.VITE_APP_DOMAIN as string | undefined) ??
      window.location.origin.replace(`${vanitySlug}.`, "");
    window.location.href = `${main.replace(/\/$/, "")}${window.location.pathname}${window.location.search}`;
  }, [vanitySlug]);
  if (vanitySlug) return <LoadingScreen />;
  if (loadingAuth) return <LoadingScreen />;
  return user ? <Outlet /> : <Navigate to={buildRoute.login()} replace />;
}

export function NotFoundPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Soft-404 mitigation: the SPA rewrite serves every unknown path with HTTP
  // 200 + the static head (canonical → home). Rendering crawlers must not
  // index junk URLs as duplicates of the homepage.
  useEffect(() => {
    const meta = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const prev = meta?.getAttribute("content") ?? null;
    meta?.setAttribute("content", "noindex");
    const canonical = document.head.querySelector('link[rel="canonical"]:not([hreflang])');
    canonical?.remove();
    return () => {
      if (prev) meta?.setAttribute("content", prev);
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-dvh bg-background p-6 text-center animate-in fade-in duration-500">
      <div className="p-4 rounded-full bg-primary/10 mb-6">
        <MapPinOff className="w-12 h-12 text-primary" />
      </div>

      <h1 className="text-3xl tracking-tight mb-3">{t("404.title")}</h1>
      <p className="text-muted-foreground max-w-sm mb-8">
        {t("404.description")}
      </p>

      <div className="flex flex-wrap gap-3">
        <Button design="outline" icon={ArrowLeft} onClick={() => navigate(-1)}>
          {t("back")}
        </Button>
        <Button icon={Home} to={user ? buildRoute.tours() : "/"}>
          {user ? t("back_dashboard") : t("back_home")}
        </Button>
      </div>
    </div>
  );
}
